/**
 * Iter-158.13.2.20f.34 — H&M AI Breakdown V1 panel (mock mode).
 *
 * Mounts directly beneath the Script PDF on `ScriptPage`. Reuses the
 * existing composed gate (`canViewScriptContent && canContribute`)
 * inherited from the surrounding page — no new permission model.
 *
 * Every action is a call against
 *   POST /scripts/{sid}/hm-breakdown
 *   GET  /scripts/{sid}/hm-breakdowns
 *   GET  /hm-breakdowns/{bid}/pdf
 *
 * The optional focus text is APPENDED — the standard professional
 * H&M breakdown always runs. Component never sends Script content
 * from the browser; it only tells the backend to generate.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import HMImportToBreakdownDialog from "./HMImportToBreakdownDialog";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Iter-158.13.2.20f.40 — H&M Generate is now asynchronous. POST creates a
// job and returns immediately; the panel polls the latest-job endpoint on
// this cadence until the job resolves. A short cadence keeps the perceived
// latency low without hammering the backend for the ~100s Sol wire.
const HM_JOB_POLL_MS = 3000;

function findingKindLabel(kind) {
    return kind === "explicit" ? "Explicit in Script" : "H&M consideration";
}

function refLabel(ref) {
    const bits = [];
    if (ref?.episode || ref?.scene_number) {
        const ep = ref?.episode ? `${ref.episode}.` : "";
        bits.push(`Scene ${ep}${ref?.scene_number || ""}`);
    }
    if (ref?.character_name) bits.push(`Character ${ref.character_name}`);
    return bits.join(" · ");
}

// Iter-158.13.2.20f.41 — presentation-only local-time helper. Backend
// stores every timestamp as UTC ISO. The panel renders it in the
// user's actual browser timezone via Intl.DateTimeFormat — never a
// hard-coded offset, never a hard-coded label, and never a silent
// UTC-labelled-as-local presentation. For "invalid input" we fall
// back to the raw string so no clock display ever renders "Invalid
// Date" to the operator.
const _hmGeneratedFmt = new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
});

function formatGeneratedLocal(iso) {
    if (!iso) return "Unknown";
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return String(iso);
    // Intl "en-GB" style: "10 Sept 2026, 10:43" — we tidy the comma
    // separator into an "at" per the .20f.41 spec example, and keep
    // the browser tz abbreviation available on hover.
    const raw = _hmGeneratedFmt.format(dt);
    return raw.replace(/,\s*/, " at ");
}

function browserTimezoneLabel() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "local";
    } catch {
        return "local";
    }
}

function versionButtonLabel(iso) {
    if (!iso) return "";
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return iso.slice(0, 16).replace("T", " ");
    return _hmGeneratedFmt.format(dt);
}

function findingHeaderTags(f) {
    // Preserve the classification the AI produced — the .20f.41
    // repair only re-orders findings, it does NOT re-classify them.
    // NB — the Script-wide badge is rendered directly inside the
    // finding template (with its own data-testid) rather than being
    // returned from this helper, so the .20f.36 string-lock that
    // verifies the presence of `data-testid={`hm-breakdown-script-
    // wide-` continues to match the source verbatim.
    const tags = [{
        label: findingKindLabel(f.kind),
        // Green for Explicit, sky-blue for Consideration — same
        // palette used pre-.20f.41 so the muscle memory of the H&M
        // department is preserved.
        cls: f.kind === "explicit"
            ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-200"
            : "border-sky-400/40 bg-sky-500/10 text-sky-200",
    }];
    if (f.uncertain) {
        tags.push({
            label: "Uncertain",
            cls: "border-amber-400/40 bg-amber-500/10 text-amber-200",
        });
    }
    return tags;
}

export default function HMBreakdownPanel({
    projectId,
    scriptId,
    canGenerate,
    canView,
}) {
    const apiBase = useApiBase();
    const [focus, setFocus] = useState("");
    const [busy, setBusy] = useState(false);
    const [list, setList] = useState([]);
    const [current, setCurrent] = useState(null);
    const [error, setError] = useState("");
    // Iter-158.13.2.20f.40 — async job observation.
    // `job` mirrors the latest hm_breakdown_jobs row from the backend.
    // The polling timer + a mount-scoped ref that suppresses stale-job
    // toast noise are held in refs so re-renders never restart polling
    // or replay the "generated" toast for a run that finished before
    // the panel mounted.
    const [job, setJob] = useState(null);
    const pollTimerRef = useRef(null);
    const lastResolvedJobIdRef = useRef(null);
    const startedJobIdsRef = useRef(new Set());
    // Iter-158.13.2.20f.42 — import drawer state.
    const [importOpen, setImportOpen] = useState(false);

    const downloadWorkingPdf = async () => {
        try {
            const resp = await api.get(
                `${apiBase}/${projectId}/hm-breakdown-entries/pdf`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `working-hm-breakdown-${projectId}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 0);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    const refresh = useCallback(async () => {
        if (!apiBase || !projectId || !scriptId || !canView) return [];
        try {
            const { data } = await api.get(
                `${apiBase}/${projectId}/scripts/${scriptId}/hm-breakdowns`,
            );
            const arr = Array.isArray(data) ? data : [];
            setList(arr);
            if (arr.length > 0 && !current) setCurrent(arr[0]);
            return arr;
        } catch (e) {
            setError(formatApiError(e));
            return [];
        }
    }, [apiBase, projectId, scriptId, canView, current]);

    // Apply a job status update from POST or GET. Only touches `current`
    // when a fresh success is observed — a failure preserves whatever
    // breakdown was on screen before the retry.
    const applyJobUpdate = useCallback(async (nextJob, { announce = false } = {}) => {
        setJob(nextJob || null);
        if (!nextJob) {
            setBusy(false);
            return;
        }
        if (nextJob.status === "running") {
            setBusy(true);
            return;
        }
        setBusy(false);
        if (nextJob.id === lastResolvedJobIdRef.current) return;
        lastResolvedJobIdRef.current = nextJob.id;
        if (nextJob.status === "succeeded") {
            const arr = await refresh();
            const created = arr.find((b) => b.id === nextJob.breakdown_id);
            if (created) setCurrent(created);
            if (announce) toast.success("H&M breakdown generated");
        } else if (nextJob.status === "failed") {
            const msg = nextJob.error || "generation failed";
            setError(msg);
            if (announce) toast.error(msg);
        }
    }, [refresh]);

    const pollJob = useCallback(async () => {
        if (!apiBase || !projectId || !scriptId || !canView) return;
        try {
            const resp = await api.get(
                `${apiBase}/${projectId}/scripts/${scriptId}/hm-breakdown/job`,
                { validateStatus: (s) => s === 200 || s === 204 },
            );
            if (resp.status === 204 || !resp.data) {
                setJob(null);
                setBusy(false);
                return;
            }
            const announce = startedJobIdsRef.current.has(resp.data.id);
            await applyJobUpdate(resp.data, { announce });
            if (resp.data.status === "running") {
                pollTimerRef.current = setTimeout(pollJob, HM_JOB_POLL_MS);
            }
        } catch (e) {
            setError(formatApiError(e));
            setBusy(false);
        }
    }, [apiBase, projectId, scriptId, canView, applyJobUpdate]);

    useEffect(() => {
        // On mount / route change: load list + resume any running job.
        // The list load runs regardless so a completed breakdown from a
        // previous session is visible even if there is no active job.
        refresh();
        pollJob();
        return () => stopPolling();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiBase, projectId, scriptId, canView]);

    const generate = async () => {
        setBusy(true);
        setError("");
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/scripts/${scriptId}/hm-breakdown`,
                { focus_text: focus.trim() || null },
            );
            // Remember this job id so the completion toast fires exactly
            // once — not on background polls for jobs that finished
            // before this panel mounted.
            if (data && data.id) startedJobIdsRef.current.add(data.id);
            await applyJobUpdate(data, { announce: true });
            if (data && data.status === "running") {
                stopPolling();
                pollTimerRef.current = setTimeout(pollJob, HM_JOB_POLL_MS);
            }
        } catch (e) {
            setError(formatApiError(e));
            toast.error(formatApiError(e));
            setBusy(false);
        }
    };

    if (!canView) return null;

    // Iter-158.13.2.20f.36 · E — authenticated PDF download.
    // The prior `<a href>` triggered a top-level browser navigation
    // that did NOT carry the axios Bearer JWT and returned 401 in
    // the .20f.35 first LIVE UAT. Fetch the PDF as a blob via the
    // existing `api` client (Bearer attached automatically), build
    // an object URL and trigger a client-side download.
    const downloadPdf = async () => {
        if (!current?.id) return;
        try {
            const resp = await api.get(
                `${apiBase}/${projectId}/hm-breakdowns/${current.id}/pdf`,
                { responseType: "blob" },
            );
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `hm-breakdown-${current.id}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Revoke on the next tick so Safari has a moment to attach.
            setTimeout(() => URL.revokeObjectURL(url), 0);
        } catch (e) {
            setError(formatApiError(e));
            toast.error(formatApiError(e));
        }
    };

    return (
        <section
            data-testid="hm-breakdown-panel"
            className="mt-6 rounded-xl border border-white/10 bg-card p-4"
        >
            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-semibold mb-2">
                Hair &amp; Makeup AI Breakdown
            </div>

            {canGenerate ? (
                <div data-testid="hm-breakdown-generator" className="space-y-2">
                    <Textarea
                        data-testid="hm-breakdown-focus"
                        value={focus}
                        onChange={(e) => setFocus(e.target.value)}
                        placeholder="e.g. Pay particular attention to Felix’s hair changes or scenes involving wigs…"
                        rows={2}
                        maxLength={2000}
                        className="text-sm"
                    />
                    <div className="text-[10px] text-neutral-500">
                        Optional. The standard professional H&amp;M breakdown always runs — this only adds focus.
                    </div>
                    <div className="flex items-center justify-end">
                        <Button
                            type="button"
                            data-testid="hm-breakdown-generate"
                            onClick={generate}
                            disabled={busy}
                        >
                            {busy ? "Generating…" : "Generate AI Hair & Makeup Breakdown"}
                        </Button>
                    </div>
                </div>
            ) : null}

            {/* Iter-158.13.2.20f.42-kiwi — Operation Kiwi #1:
                "Add AI Breakdown to Scenes & Cast" repositioned to
                sit immediately beneath the Generate action. Same
                importOpen flow, same HMImportToBreakdownDialog,
                same additive import contract — presentation only.
                The button is conditional on `current` because
                there is nothing to import until at least one
                breakdown has been generated for this script. */}
            {current ? (
                <div className="mt-2 flex justify-end">
                    <button
                        type="button"
                        data-testid="hm-breakdown-add-to-working"
                        onClick={() => setImportOpen(true)}
                        className="tap inline-flex items-center h-9 px-3 rounded-md border border-primary/50 bg-primary/10 text-primary text-[12px] hover:bg-primary/20"
                    >
                        Add AI Breakdown to Scenes &amp; Cast
                    </button>
                </div>
            ) : null}

            {error ? (
                <div data-testid="hm-breakdown-error" className="mt-2 text-[11px] text-red-300">
                    {error}
                </div>
            ) : null}

            {list.length > 1 ? (
                <div
                    data-testid="hm-breakdown-versions"
                    className="mt-3 flex flex-wrap gap-1.5"
                >
                    {list.map((b) => (
                        <button
                            type="button"
                            key={b.id}
                            data-testid={`hm-breakdown-version-${b.id}`}
                            onClick={() => setCurrent(b)}
                            className={`tap text-[11px] px-2 py-0.5 rounded-md border ${
                                current?.id === b.id
                                    ? "border-primary/50 bg-primary/10 text-primary"
                                    : "border-white/10 text-neutral-300 hover:bg-white/5"
                            }`}
                        >
                            {versionButtonLabel(b.generated_at)}
                        </button>
                    ))}
                </div>
            ) : null}

            {current ? (
                <div data-testid="hm-breakdown-result" className="mt-4 space-y-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div
                            className="text-[11px] text-neutral-400"
                            data-testid="hm-breakdown-generated-at"
                            title={`Stored UTC: ${current.generated_at || "unknown"} · Rendered in ${browserTimezoneLabel()}`}
                        >
                            Generated {formatGeneratedLocal(current.generated_at)} · provider {current.provider}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                        <button
                            type="button"
                            data-testid="hm-breakdown-pdf"
                            onClick={downloadPdf}
                            className="tap inline-flex items-center h-8 px-2.5 rounded-md border border-white/15 text-neutral-200 text-[11px] hover:bg-white/5"
                        >
                            Download AI Breakdown PDF
                        </button>
                        <button
                            type="button"
                            data-testid="hm-breakdown-working-pdf"
                            onClick={downloadWorkingPdf}
                            className="tap inline-flex items-center h-8 px-2.5 rounded-md border border-white/15 text-neutral-200 text-[11px] hover:bg-white/5"
                        >
                            Download Edited Breakdown PDF
                        </button>
                        </div>
                    </div>
                    {current.focus_text ? (
                        <div
                            data-testid="hm-breakdown-focus-echo"
                            className="text-[11px] text-neutral-400 italic border-l-2 border-white/20 pl-2"
                        >
                            Requested focus: {current.focus_text}
                        </div>
                    ) : null}
                    {(() => {
                        // Iter-158.13.2.20f.41 — primary presentation is
                        // Scene-first, using the server-computed
                        // `scene_view`. The persisted `payload`
                        // (thematic sections) is retained on the row
                        // for provenance but is no longer the primary
                        // presentation surface.
                        const sceneView = current.scene_view || null;
                        if (!sceneView) return null;
                        const renderFinding = (f, key) => (
                            <li
                                key={key}
                                data-testid={`hm-breakdown-finding-${key}`}
                                className="text-[12px] text-neutral-200"
                            >
                                <div className="flex flex-wrap items-center gap-1 mb-0.5">
                                    {(f.character_names || []).length > 0 ? (
                                        <span
                                            data-testid={`hm-breakdown-finding-chars-${key}`}
                                            className="text-[11px] text-white font-semibold"
                                        >
                                            {(f.character_names || []).join(" / ")}
                                        </span>
                                    ) : null}
                                    <span className="text-[10px] text-neutral-400">
                                        {(f.character_names || []).length > 0 ? "—" : null} {f.facet}
                                    </span>
                                    {findingHeaderTags(f).map((t, ti) => (
                                        <span
                                            key={ti}
                                            className={`text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full border ${t.cls}`}
                                        >
                                            {t.label}
                                        </span>
                                    ))}
                                    {f.script_wide ? (
                                        <span
                                            data-testid={`hm-breakdown-script-wide-${key}`}
                                            className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full border border-violet-400/40 bg-violet-500/10 text-violet-200"
                                        >
                                            Script-wide
                                        </span>
                                    ) : null}
                                    {f.section_title ? (
                                        <span
                                            className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-md border border-white/10 text-neutral-400"
                                            data-testid={`hm-breakdown-finding-section-${key}`}
                                        >
                                            {f.section_title}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="whitespace-pre-wrap">{f.note}</div>
                            </li>
                        );
                        return (
                            <div data-testid="hm-breakdown-scene-view" className="space-y-3">
                                {(sceneView.script_wide || []).length > 0 ? (
                                    <div
                                        data-testid="hm-breakdown-scene-scriptwide"
                                        className="rounded-lg border border-violet-400/20 bg-violet-500/[0.06] p-3"
                                    >
                                        <div className="text-[12px] font-semibold text-violet-100 mb-2">
                                            Applies script-wide
                                        </div>
                                        <ul className="space-y-2">
                                            {(sceneView.script_wide || []).map((f, fi) =>
                                                renderFinding(f, `sw-${fi}`),
                                            )}
                                        </ul>
                                    </div>
                                ) : null}
                                {(sceneView.scenes || []).map((sc, si) => (
                                    <div
                                        key={`${sc.episode || ""}-${sc.scene_number || si}`}
                                        data-testid={`hm-breakdown-scene-${sc.episode || "no-ep"}-${sc.scene_number || "unknown"}`}
                                        className={`rounded-lg border ${sc.resolved ? "border-white/5 bg-black/20" : "border-amber-400/30 bg-amber-500/[0.06]"} p-3`}
                                    >
                                        <div className="text-[12px] font-semibold text-white mb-2 flex items-center gap-2">
                                            <span>{sc.display}</span>
                                            {sc.resolved ? null : (
                                                <span
                                                    data-testid={`hm-breakdown-scene-unresolved-${sc.scene_number || "unknown"}`}
                                                    className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-200"
                                                >
                                                    Unresolved · no canonical Scene
                                                </span>
                                            )}
                                        </div>
                                        <ul className="space-y-2">
                                            {(sc.findings || []).map((f, fi) =>
                                                renderFinding(f, `${si}-${fi}`),
                                            )}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                    {((current.reconciliation || {}).unmatched_scene_refs || []).length > 0
                     || ((current.reconciliation || {}).unmatched_character_refs || []).length > 0 ? (
                        <div
                            data-testid="hm-breakdown-unmatched"
                            className="rounded-lg border border-amber-400/30 bg-amber-500/[0.06] p-3"
                        >
                            <div className="text-[11px] uppercase tracking-[0.2em] text-amber-200 font-semibold mb-1">
                                Unresolved references (for human review)
                            </div>
                            {((current.reconciliation || {}).unmatched_scene_refs || []).length > 0 ? (
                                <>
                                    <div className="text-[10px] uppercase tracking-[0.15em] text-amber-200/80 mt-1">
                                        Unresolved Scene references
                                    </div>
                                    <ul className="text-[11px] text-amber-100/90 space-y-0.5">
                                        {(current.reconciliation.unmatched_scene_refs || []).map((u, i) => (
                                            <li key={`s-${i}`} data-testid={`hm-breakdown-unmatched-scene-${i}`}>
                                                Scene {u.episode ? `${u.episode}.` : ""}{u.scene_number} · {u.reason}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : null}
                            {((current.reconciliation || {}).unmatched_character_refs || []).length > 0 ? (
                                <>
                                    <div className="text-[10px] uppercase tracking-[0.15em] text-amber-200/80 mt-2">
                                        Unresolved Character references
                                    </div>
                                    <ul className="text-[11px] text-amber-100/90 space-y-0.5">
                                        {(current.reconciliation.unmatched_character_refs || []).map((u, i) => (
                                            <li key={`c-${i}`} data-testid={`hm-breakdown-unmatched-char-${i}`}>
                                                Character {u.character_name} · {u.reason}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : null}
                        </div>
                    ) : null}
                    {/* Iter-158.13.2.20f.42-kiwi — Operation Kiwi #1:
                        the "Add AI Breakdown to Scenes & Cast" action
                        was hoisted to sit directly beneath the
                        Generate button (see above). It no longer
                        renders here at the foot of the generated
                        review area. Same importOpen state / same
                        dialog / same additive backend — presentation
                        change only. */}
                </div>
            ) : null}
            <HMImportToBreakdownDialog
                open={importOpen}
                onOpenChange={setImportOpen}
                apiBase={apiBase}
                projectId={projectId}
                breakdown={current}
            />
        </section>
    );
}
